"use client";

import React, { useCallback, useRef, useState } from "react";
import {
  AgentWidget,
  AgentMessage as AgentMessageType,
  PresetOption,
} from "@/types/createAgent";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useCreatePageStore } from "@/stores/createPageStore";
import { getWidgetPresets } from "@/lib/agent/widgetPresets";
import { isPresentableChatWidget } from "@/lib/agent/renderableChatMessage";
import {
  getRendererPresentedMessagePresets,
  getRendererPresentedWidgetPresets,
} from "@/lib/agent/visiblePresetCapabilities";
import {
  isDesktopFooterOwnedPreset,
  isDesignerRenderCommitPreset,
  type DesignerRenderActionId,
} from "@/lib/agent/designerActionPresentation";
import {
  isCommitPresetAction,
  normalizePresetLabel,
} from "@/lib/agent/presetPresentation";
import { isWorkspaceSecondaryAction } from "@/lib/designer/workspaceActions";
import type { AgentActionPayload } from "@/types/createAgent";
import type { DesktopRenderActionState } from "../desktopRenderAction";
import AgentPresets from "../presets/AgentPresets";
import AgentStatusDot from "../AgentStatusDot";
import { useDesignerWorkspace } from "../DesignerWorkspaceProvider";
import AgentMarkdownContent from "./AgentMarkdownContent";
import MessageImageAttachment from "./MessageImageAttachment";
import MessageImageModal from "./MessageImageModal";
import MessageSources from "./MessageSources";
import SelectionReceipt from "./SelectionReceipt";

interface AgentMessageProps {
  message: AgentMessageType;
  /** When provided, clicking the image opens the header's full-size modal (next to Start Over) with this view instead of the message's local modal */
  onOpenFullSizeImage?: (initialView: "front" | "back") => void;
  presetOptions?: PresetOption[];
  widgetOptions?: AgentWidget[];
  onPresetClick?: (
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => void;
  onPresetSelect?: (
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => void;
  onPresetGenerate?: (
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => void;
  onPresetChatMore?: (
    preset: PresetOption | null,
    source?: AgentActionPayload["source"]
  ) => void;
  onComposerModifyRequest?: () => void;
  onComposerModifyExit?: () => void;
  presetsActive?: boolean;
  presetsDisabled?: boolean;
  presetsLoading?: boolean;
  processingPresetId?: string | null;
  selectedPresetLabel?: string | null;
  /** Entrance animation only on the newest message — restored history must not cascade in. */
  isLatest?: boolean;
  freeFrontPreview?: boolean;
  /** Exact render action duplicated by the stable desktop footer. */
  desktopFooterActionId?: DesignerRenderActionId | null;
  /** The footer is resolving this surface and temporarily owns render commits. */
  desktopFooterAuthorityPending?: boolean;
  onDesktopRenderActionStateChange?: (state: DesktopRenderActionState) => void;
}

function formatTimestamp(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function AgentMessage({
  message,
  onOpenFullSizeImage,
  presetOptions,
  widgetOptions,
  onPresetClick,
  onPresetSelect,
  onPresetGenerate,
  onPresetChatMore,
  onComposerModifyRequest,
  onComposerModifyExit,
  presetsActive = false,
  presetsDisabled = false,
  presetsLoading = false,
  processingPresetId = null,
  selectedPresetLabel = null,
  isLatest = false,
  freeFrontPreview = false,
  desktopFooterActionId,
  desktopFooterAuthorityPending = false,
  onDesktopRenderActionStateChange,
}: AgentMessageProps) {
  const sharedWorkspace = useDesignerWorkspace()?.sharedWorkspace === true;
  const agentSessionId = useCreatePageStore(state => state.agent.sessionId);
  const isUser = message.role === "user";
  const hasText = !!(message.content && message.content.trim().length > 0);
  const imageUrls =
    message.imageUrls || (message.imageUrl ? [message.imageUrl] : []);
  const hasImage = imageUrls.length > 0;
  const hasSources = (message.sources?.length ?? 0) > 0;
  const safePresetOptions = presetOptions || [];
  const safeWidgetOptions = widgetOptions || [];
  // A submitted capability is deliberately stamped `active: false` before
  // the next turn is appended. Rendering that consumed button again as a
  // dimmed historical control repeats the previous decision and, after a
  // checkout return, can misleadingly advertise the action the customer just
  // completed. Keep inert informational widgets (for example Saved details),
  // but remove consumed action widgets from the transcript.
  const presentableWidgetOptions = safeWidgetOptions.filter(
    isPresentableChatWidget
  );
  // No loading skeleton here: while the turn is still thinking, the thinking
  // trace carries the progress signal — the action card only appears once
  // real actions exist.
  const hasPresetSurface =
    !isUser &&
    (safePresetOptions.length > 0 || presentableWidgetOptions.length > 0);
  // Widget plans are renderer-authoritative when present; message-level
  // presets are compatibility data and may contain actions the widget renderer
  // deliberately suppresses.
  const surfaceEntries =
    presentableWidgetOptions.length > 0
      ? presentableWidgetOptions.flatMap(widget =>
          getRendererPresentedWidgetPresets(widget).map(preset => ({
            preset,
            widget,
          }))
        )
      : getRendererPresentedMessagePresets(
          safePresetOptions,
          selectedPresetLabel
        ).map(preset => ({ preset, widget: null }));
  const surfacePresets = surfaceEntries.map(entry => entry.preset);
  const containsCostlyAction = surfacePresets.some(preset =>
    isDesignerRenderCommitPreset(preset)
  );
  const containsInformationalWidget = presentableWidgetOptions.some(
    widget => getWidgetPresets(widget).length === 0
  );
  const desktopFooterOwnsAllActions =
    surfaceEntries.length > 0 &&
    surfaceEntries.every(
      ({ preset, widget }) =>
        (isWorkspaceSecondaryAction(preset.actionId) &&
          (!sharedWorkspace ||
            !widget ||
            widget.id === "v2-review-action-panel-standing")) ||
        ((sharedWorkspace ||
          desktopFooterAuthorityPending ||
          preset.actionId === desktopFooterActionId) &&
          isDesktopFooterOwnedPreset(preset))
    );
  const desktopFooterOwnsEntireSurface =
    !containsInformationalWidget && desktopFooterOwnsAllActions;
  const desktopPresetHeaderIsMobileOnly =
    containsInformationalWidget && desktopFooterOwnsAllActions;
  const showActivePresets = hasPresetSurface && presetsActive;
  // Once the customer moves on, the assistant question plus their next turn
  // are the durable transcript. Repeating old interactive controls as disabled
  // cards competes with the one current decision and can consume most of a
  // mobile viewport. Keep only inert informational disclosures in history;
  // selected choices use the compact receipt below.
  const showInactivePresets =
    hasPresetSurface &&
    !presetsActive &&
    !presetsLoading &&
    !isUser &&
    surfacePresets.length === 0 &&
    !selectedPresetLabel;
  const selectedPresetIsCommit = Boolean(
    selectedPresetLabel &&
    surfacePresets.some(
      preset =>
        isCommitPresetAction(preset.actionId) &&
        normalizePresetLabel(preset.label) ===
          normalizePresetLabel(selectedPresetLabel)
    )
  );
  const showSelectionReceipt =
    !isUser &&
    !presetsActive &&
    !presetsLoading &&
    !!selectedPresetLabel &&
    !selectedPresetIsCommit;
  const presetHeader = containsCostlyAction ? "Next step" : "Design options";
  const hasAuthoredWidgetHeading = presentableWidgetOptions.some(
    widget => Boolean(widget.title?.trim()) || Boolean(widget.summary?.trim())
  );
  const showPresetHeader = containsCostlyAction || !hasAuthoredWidgetHeading;
  const [activeImageIndex, setActiveImageIndex] = useState(
    message.selectedIndex || 0
  );
  const [showFullSizeModal, setShowFullSizeModal] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const displayedImageKeysRef = useRef(new Set<string>());

  const handleImageLoad = useCallback(
    (loadedImageUrl: string) => {
      if (isUser || !agentSessionId) return;

      const imageKey = `${agentSessionId}:${loadedImageUrl}`;
      if (displayedImageKeysRef.current.has(imageKey)) return;
      displayedImageKeysRef.current.add(imageKey);

      // Only claim a side when the message actually carries one. Guessing from
      // position mislabels the second of two front variations as "back", and
      // the event route rejects a side that does not match the owning ticket,
      // so a wrong guess silently drops the event. Omitting the field lets the
      // server resolve the side from the ticket it already looked up.
      void fetch("/api/create/agent/event", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_type: "render_displayed",
          session_id: agentSessionId,
          image_url: loadedImageUrl,
          ...(message.imageSide && { image_side: message.imageSide }),
        }),
        keepalive: true,
      }).catch(() => {
        // Telemetry must never interfere with displaying the render.
      });
    },
    [agentSessionId, isUser, message.imageSide]
  );

  const handleImageClick = (imageIndex = activeImageIndex) => {
    // The header viewer is bound to generated front/back renders. Reference
    // uploads are independent images, so user turns always use their local
    // gallery modal instead.
    if (onOpenFullSizeImage && !isUser) {
      const initialView: "front" | "back" =
        message.imageSide ??
        (imageUrls.length === 2 && imageIndex === 1 ? "back" : "front");
      onOpenFullSizeImage(initialView);
      return;
    }
    setModalImageIndex(imageIndex);
    setShowFullSizeModal(true);
  };

  const imageModal = (
    <MessageImageModal
      open={showFullSizeModal}
      onClose={() => setShowFullSizeModal(false)}
      imageUrls={imageUrls}
      imageIndex={modalImageIndex}
      setImageIndex={setModalImageIndex}
    />
  );

  // User turns stay as compact bubbles; assistant turns render as open prose
  // on the chat background so replies read like a conversation, not a stack
  // of nested cards.
  if (isUser) {
    return (
      <div
        data-agent-message="user"
        title={formatTimestamp(message.timestamp)}
        className={cn("flex justify-end", isLatest && "animate-slide-up")}
      >
        <div className="group ml-6 min-w-0 max-w-[90%] sm:ml-10 sm:max-w-[85%]">
          <Card
            variant="message-user"
            padding="none"
            className="relative rounded-2xl rounded-br-md px-4 py-3 shadow-none"
          >
            {hasText && (
              <div className="text-sm leading-relaxed wrap-break-word">
                <p className="whitespace-pre-wrap">{message.content}</p>
              </div>
            )}

            {hasImage && (
              <MessageImageAttachment
                imageUrls={imageUrls}
                activeImageIndex={activeImageIndex}
                hasText={hasText}
                content={message.content}
                onImageClick={handleImageClick}
                onSelectImage={setActiveImageIndex}
                variant="reference"
              />
            )}

            {imageModal}

            <span className="sr-only">
              {formatTimestamp(message.timestamp)}
            </span>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div
      data-agent-message="assistant"
      title={formatTimestamp(message.timestamp)}
      className={cn("flex justify-start", isLatest && "animate-slide-up")}
    >
      <div className="w-full min-w-0 pr-1 sm:pr-4 group">
        {hasText && (
          <div className="text-[15px] leading-relaxed text-foreground wrap-break-word">
            <AgentMarkdownContent
              content={message.content || ""}
              trailing={
                hasSources ? (
                  <MessageSources sources={message.sources!} inline />
                ) : undefined
              }
            />
          </div>
        )}

        {hasText && (
          <span className="sr-only">{formatTimestamp(message.timestamp)}</span>
        )}

        {hasImage && (
          <div className={hasText ? "mt-3" : undefined}>
            <MessageImageAttachment
              imageUrls={imageUrls}
              activeImageIndex={activeImageIndex}
              hasText={hasText}
              content={message.content}
              onImageClick={handleImageClick}
              onImageLoad={handleImageLoad}
              onSelectImage={setActiveImageIndex}
            />
          </div>
        )}

        {hasSources && !hasText && (
          <MessageSources sources={message.sources!} />
        )}

        {showSelectionReceipt && (
          <SelectionReceipt selectedPresetLabel={selectedPresetLabel} />
        )}

        {(showActivePresets || showInactivePresets) && (
          <div
            data-agent-presets-state={showActivePresets ? "active" : "inactive"}
            data-desktop-action-owner={
              desktopFooterOwnsEntireSurface ? "footer" : undefined
            }
            className={cn(
              // One card packages the choices and their commit actions
              // together; the tiles/buttons inside are the only other layer.
              "mt-4 rounded-2xl border border-border bg-card/70 p-3 shadow-sm sm:p-4",
              desktopFooterOwnsEntireSurface &&
                (sharedWorkspace ? "hidden" : "lg:hidden"),
              // Action controls receive a real disabled state below. Avoid a
              // blanket pointer-events lock here so historical informational
              // disclosures (for example Saved details) remain inspectable.
              showInactivePresets && "opacity-55 grayscale-[0.15]"
            )}
          >
            {showPresetHeader ? (
              <div
                className={cn(
                  "mb-2.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider",
                  desktopPresetHeaderIsMobileOnly &&
                    (sharedWorkspace ? "hidden" : "lg:hidden"),
                  presetsActive ? "text-brand-logo" : "text-muted-foreground"
                )}
              >
                <AgentStatusDot variant={presetsActive ? "primary" : "muted"} />
                <span className="truncate">{presetHeader}</span>
              </div>
            ) : null}
            <AgentPresets
              messageId={message.id}
              presets={safePresetOptions}
              widgets={presentableWidgetOptions}
              onPresetClick={(preset, source) =>
                source
                  ? onPresetClick?.(preset, source)
                  : onPresetClick?.(preset)
              }
              onPresetSelect={(preset, source) =>
                source
                  ? onPresetSelect?.(preset, source)
                  : onPresetSelect?.(preset)
              }
              onPresetGenerate={(preset, source) =>
                source
                  ? onPresetGenerate?.(preset, source)
                  : onPresetGenerate?.(preset)
              }
              onPresetChatMore={(preset, source) =>
                source
                  ? onPresetChatMore?.(preset, source)
                  : onPresetChatMore?.(preset)
              }
              onComposerModifyRequest={onComposerModifyRequest}
              onComposerModifyExit={onComposerModifyExit}
              disabled={
                presetsDisabled ||
                !presetsActive ||
                presetsLoading ||
                !onPresetClick
              }
              processingPresetId={processingPresetId}
              selectedPresetLabel={selectedPresetLabel}
              freeFrontPreview={freeFrontPreview}
              desktopFooterActionId={desktopFooterActionId}
              desktopFooterAuthorityPending={desktopFooterAuthorityPending}
              onDesktopRenderActionStateChange={
                onDesktopRenderActionStateChange
              }
            />
          </div>
        )}

        {imageModal}

        {!hasText && (
          <span className="sr-only">{formatTimestamp(message.timestamp)}</span>
        )}
      </div>
    </div>
  );
}
